import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSession } from '@/lib/SessionContext';
import { supabase } from '@/api/base44Client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Eye, EyeOff, QrCode, KeyRound, AlertCircle, Camera, X } from 'lucide-react';

export default function TeamLogin() {
  const navigate = useNavigate();
  const { login, member } = useSession();

  const [mode, setMode] = useState('password'); // 'password' | 'qr_id'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [employeeId, setEmployeeId] = useState('');
  const [qrIdTab, setQrIdTab] = useState('id'); // 'id' | 'qr'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [scanning, setScanning] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    if (member) {
      const queryParams = new URLSearchParams(window.location.search);
      const redirectPath = queryParams.get('redirect');
      if (redirectPath) {
        navigate(redirectPath);
      } else {
        navigate('/office');
      }
    }
  }, [member, navigate]);

  useEffect(() => {
    return () => stopCamera();
  }, []);

  const stopCamera = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setScanning(false);
  };

  const startCamera = async () => {
    setError('');
    setScanning(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
      const interval = setInterval(async () => {
        if (!videoRef.current) {
          clearInterval(interval);
          return;
        }
        const canvas = document.createElement('canvas');
        canvas.width = videoRef.current.videoWidth;
        canvas.height = videoRef.current.videoHeight;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(videoRef.current, 0, 0);
          canvas.toBlob(async (blob) => {
            if (!blob) return;
            if ('BarcodeDetector' in window) {
              const detector = new window.BarcodeDetector({ formats: ['qr_code'] });
              try {
                const bitmapSource = await createImageBitmap(blob);
                const codes = await detector.detect(bitmapSource);
                if (codes.length > 0) {
                  clearInterval(interval);
                  stopCamera();
                  await handleQRResult(codes[0].rawValue);
                }
              } catch {}
            }
          }, 'image/jpeg');
        }
      }, 300);
    } catch (err) {
      setError('Gagal mengakses kamera. Pastikan izin kamera diberikan.');
      setScanning(false);
    }
  };

  const handleQRResult = async (rawValue) => {
    setLoading(true);
    setError('');
    try {
      const { data, error } = await supabase.from('TeamMember').select('*');
      if (error) throw new Error(error.message);

      const matched = data?.find(row => {
        const item = { ...row.data, id: row.id };
        return (item.employee_id || '').trim().toLowerCase() === rawValue.trim().toLowerCase();
      });

      if (matched) {
        const isDeleted = Boolean(matched.data?.deleted_at) || 
                          ['inactive', 'nonaktif', 'deleted'].includes((matched.data?.status || '').toLowerCase());
        if (isDeleted) {
          setError('Akun ini telah dinonaktifkan atau dihapus dari sistem.');
          return;
        }

        const memberData = { ...matched.data, id: matched.id, created_date: matched.created_date, updated_date: matched.updated_date };
        login(memberData);
        const queryParams = new URLSearchParams(window.location.search);
        const redirectPath = queryParams.get('redirect') || '/office';
        navigate(redirectPath);
      } else {
        setError('QR Code / ID Karyawan tidak terdaftar.');
      }
    } catch (err) {
      console.error(err);
      setError('Gagal memverifikasi QR: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const { data, error } = await supabase.from('TeamMember').select('*');
      if (error) throw new Error(error.message);

      const matched = data?.find(row => {
        const item = { ...row.data, id: row.id };
        return (item.email || '').trim().toLowerCase() === email.trim().toLowerCase() &&
               (item.password || '') === password;
      });

      if (matched) {
        const isDeleted = Boolean(matched.data?.deleted_at) || 
                          ['inactive', 'nonaktif', 'deleted'].includes((matched.data?.status || '').toLowerCase());
        if (isDeleted) {
          setError('Akun ini telah dinonaktifkan atau dihapus dari sistem.');
          return;
        }

        const memberData = { ...matched.data, id: matched.id, created_date: matched.created_date, updated_date: matched.updated_date };
        login(memberData);
        const queryParams = new URLSearchParams(window.location.search);
        const redirectPath = queryParams.get('redirect') || '/office';
        navigate(redirectPath);
      } else {
        setError('Email atau password salah.');
      }
    } catch (err) {
      console.error(err);
      setError('Gagal login: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEmployeeIdLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const { data, error } = await supabase.from('TeamMember').select('*');
      if (error) throw new Error(error.message);

      const matched = data?.find(row => {
        const item = { ...row.data, id: row.id };
        return (item.employee_id || '').trim().toLowerCase() === employeeId.trim().toLowerCase();
      });

      if (matched) {
        const isDeleted = Boolean(matched.data?.deleted_at) || 
                          ['inactive', 'nonaktif', 'deleted'].includes((matched.data?.status || '').toLowerCase());
        if (isDeleted) {
          setError('Akun ini telah dinonaktifkan atau dihapus dari sistem.');
          return;
        }

        const memberData = { ...matched.data, id: matched.id, created_date: matched.created_date, updated_date: matched.updated_date };
        login(memberData);
        const queryParams = new URLSearchParams(window.location.search);
        const redirectPath = queryParams.get('redirect') || '/office';
        navigate(redirectPath);
      } else {
        setError('ID Karyawan tidak terdaftar.');
      }
    } catch (err) {
      console.error(err);
      setError('Gagal login: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#fffcf8] flex items-center justify-center p-4 md:p-6 font-sans">
      <div className="w-full max-w-[320px] md:max-w-[420px] flex flex-col items-center">
        
        {/* Satala Logo */}
        <div className="flex flex-col items-center mb-6 md:mb-8">
          <div className="flex items-center gap-2 md:gap-3 mb-3 md:mb-4">
            <div className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center shrink-0">
              <img src="/logo.png" alt="Satala Logo" className="w-full h-full object-contain drop-shadow-sm" />
            </div>
            <div className="flex flex-col items-start">
              <span className="text-[18px] md:text-[22px] font-black text-[#302621] leading-tight tracking-tight">Satala Office</span>
              <span className="text-[7.5px] md:text-[9px] text-[#8C6208] font-black tracking-[0.15em] uppercase mt-0.5 leading-none">PUSAT INFORMASI & KENDALI</span>
            </div>
          </div>
          <div className="w-full h-px bg-slate-200 mb-3 md:mb-4"></div>
          <h1 className="text-sm md:text-base font-bold text-[#302621] tracking-tight">Login Tim Satala</h1>
          <p className="text-slate-500 text-[10px] md:text-xs mt-1 text-center font-medium">Masuk untuk melihat orderan dan tugas harian anda</p>
        </div>

        {/* Toggle Mode Tabs (Email & Password / Scan QR / ID) */}
        <div className="w-full bg-[#EAEAEA]/60 p-1 rounded-[10px] md:rounded-xl flex gap-1 mb-5 md:mb-6">
          <button
            onClick={() => { setMode('password'); setError(''); stopCamera(); }}
            className={`flex-1 flex items-center justify-center gap-1.5 md:gap-2 py-1.5 md:py-2 rounded-md md:rounded-lg text-[10px] md:text-xs font-bold transition-all ${
              mode === 'password' ? 'bg-white shadow-sm text-[#302621]' : 'text-slate-500 hover:text-slate-800'
            }`}
          >
            <KeyRound className="w-3 h-3 md:w-3.5 md:h-3.5" />
            <span>Email & Password</span>
          </button>
          <button
            onClick={() => { setMode('qr_id'); setError(''); stopCamera(); }}
            className={`flex-1 flex items-center justify-center gap-1.5 md:gap-2 py-1.5 md:py-2 rounded-md md:rounded-lg text-[10px] md:text-xs font-bold transition-all ${
              mode === 'qr_id' ? 'bg-white shadow-sm text-[#302621]' : 'text-slate-500 hover:text-slate-800'
            }`}
          >
            <QrCode className="w-3 h-3 md:w-3.5 md:h-3.5" />
            <span>Scan QR / ID</span>
          </button>
        </div>

        {/* Card Form */}
        <div className="w-full bg-white border border-[#EBEBEB] rounded-xl md:rounded-2xl p-4 md:p-6 shadow-sm space-y-3 md:space-y-4">
          
          {error && (
            <div className="flex items-center gap-2 text-[10px] md:text-xs text-red-600 bg-red-50 rounded-lg p-2 md:p-3">
              <AlertCircle className="w-3.5 h-3.5 md:w-4 md:h-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* MODE: Email & Password */}
          {mode === 'password' && (
            <form onSubmit={handlePasswordLogin} className="space-y-3 md:space-y-4">
              <div>
                <Label className="text-[10px] md:text-xs font-bold text-slate-500 mb-1 block">Email</Label>
                <Input
                  type="email"
                  placeholder="email@perusahaan.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value.toLowerCase())}
                  required
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck="false"
                  className="h-9 md:h-10 text-[11px] md:text-xs border-slate-200 rounded-lg focus:ring-2 focus:ring-[#8C6208]"
                />
              </div>

              <div>
                <Label className="text-[10px] md:text-xs font-bold text-slate-500 mb-1 block">Password</Label>
                <div className="relative">
                  <Input
                    type={showPass ? 'text' : 'password'}
                    placeholder="Password..."
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck="false"
                    className="h-9 md:h-10 text-[11px] md:text-xs border-slate-200 rounded-lg pr-9 md:pr-10 focus:ring-2 focus:ring-[#8C6208]"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPass(!showPass)}
                    className="absolute right-2.5 md:right-3 top-2.5 md:top-3 text-slate-400 hover:text-slate-600"
                  >
                    {showPass ? <EyeOff className="w-3.5 h-3.5 md:w-4 md:h-4" /> : <Eye className="w-3.5 h-3.5 md:w-4 md:h-4" />}
                  </button>
                </div>
              </div>

              <Button type="submit" disabled={loading} className="w-full h-10 md:h-11 bg-[#302621] hover:bg-[#1A1411] text-white rounded-lg text-[11px] md:text-xs font-bold tracking-wider transition-colors shadow-sm mt-1 md:mt-2">
                {loading ? 'Memverifikasi...' : 'Masuk'}
              </Button>
            </form>
          )}

          {/* MODE: Scan QR / ID */}
          {mode === 'qr_id' && (
            <div className="space-y-3 md:space-y-4">
              <div className="flex gap-1 p-1 bg-slate-50 rounded-lg">
                <button
                  onClick={() => { setQrIdTab('id'); setError(''); stopCamera(); }}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-md text-[9px] md:text-[10px] font-bold transition-all ${
                    qrIdTab === 'id' ? 'bg-white shadow-xs text-[#302621]' : 'text-slate-400 hover:text-slate-600'
                  }`}
                >
                  Masukkan ID
                </button>
                <button
                  onClick={() => { setQrIdTab('qr'); setError(''); }}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-md text-[9px] md:text-[10px] font-bold transition-all ${
                    qrIdTab === 'qr' ? 'bg-white shadow-xs text-[#302621]' : 'text-slate-400 hover:text-slate-600'
                  }`}
                >
                  Scan QR
                </button>
              </div>

              {qrIdTab === 'id' && (
                <form onSubmit={handleEmployeeIdLogin} className="space-y-3 md:space-y-4">
                  <div>
                    <Label className="text-[10px] md:text-xs font-bold text-slate-500 mb-1 block">ID Karyawan</Label>
                    <Input
                      placeholder="Contoh: EMP001"
                      value={employeeId}
                      onChange={(e) => setEmployeeId(e.target.value.toLowerCase())}
                      required
                      autoCapitalize="none"
                      autoCorrect="off"
                      spellCheck="false"
                      className="h-10 md:h-11 text-center font-mono tracking-widest text-[11px] md:text-sm border-slate-200 rounded-lg focus:ring-2 focus:ring-[#8C6208]"
                    />
                    <p className="text-[9px] md:text-[10px] text-slate-400 text-center mt-1.5 md:mt-2">ID Karyawan tertera di kartu ID Anda</p>
                  </div>
                  <Button type="submit" disabled={loading} className="w-full h-10 md:h-11 bg-[#302621] hover:bg-[#1A1411] text-white rounded-lg text-[11px] md:text-xs font-bold tracking-wider transition-colors shadow-sm">
                    {loading ? 'Memverifikasi...' : 'Masuk dengan ID'}
                  </Button>
                </form>
              )}

              {qrIdTab === 'qr' && (
                <div className="space-y-2.5 md:space-y-3">
                  {scanning ? (
                    <div className="space-y-2.5 md:space-y-3">
                      <div className="relative rounded-xl overflow-hidden bg-black aspect-square">
                        <video ref={videoRef} className="w-full h-full object-cover" muted playsInline />
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          <div className="w-24 h-24 md:w-32 md:h-32 border-2 border-[#8C6208] rounded-xl relative">
                            <div className="absolute top-0 left-0 w-3 h-3 md:w-4 md:h-4 border-t-2 border-l-2 border-[#8C6208]" />
                            <div className="absolute top-0 right-0 w-3 h-3 md:w-4 md:h-4 border-t-2 border-r-2 border-[#8C6208]" />
                            <div className="absolute bottom-0 left-0 w-3 h-3 md:w-4 md:h-4 border-b-2 border-l-2 border-[#8C6208]" />
                            <div className="absolute bottom-0 right-0 w-3 h-3 md:w-4 md:h-4 border-b-2 border-r-2 border-[#8C6208]" />
                          </div>
                        </div>
                      </div>
                      <Button variant="outline" className="w-full text-[10px] md:text-xs font-bold h-9 md:h-10 gap-2 border-red-200 text-red-600 hover:bg-red-50" onClick={stopCamera}>
                        <X className="w-3.5 h-3.5 md:w-4 md:h-4" /> Batal
                      </Button>
                    </div>
                  ) : (
                    <div className="text-center py-3 md:py-4 space-y-3 md:space-y-4">
                      <div className="w-12 h-12 md:w-16 md:h-16 mx-auto bg-slate-50 border border-slate-100 rounded-[14px] md:rounded-2xl flex items-center justify-center">
                        <Camera className="w-6 h-6 md:w-8 md:h-8 text-slate-300" />
                      </div>
                      <div>
                        <p className="text-[10px] md:text-xs font-bold text-slate-700">Scan QR Card Karyawan</p>
                        <p className="text-[8.5px] md:text-[10px] text-slate-400 mt-1">Gunakan kamera perangkat Anda untuk memindai kartu QR.</p>
                      </div>
                      <Button className="w-full h-10 md:h-11 bg-[#302621] hover:bg-[#1A1411] text-white rounded-lg text-[11px] md:text-xs font-bold" onClick={startCamera}>
                        Buka Kamera
                      </Button>
                    </div>
                  )}
                </div>
              )}

            </div>
          )}

        </div>

        <p className="text-center text-[9px] md:text-[10px] text-slate-400 mt-4 md:mt-6">Hubungi admin jika lupa password atau ID karyawan</p>
      </div>
    </div>
  );
}
